import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Copy, Ban } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const field = "h-10 rounded-lg bg-zinc-900/60 border border-white/10 px-3 text-sm outline-none focus:border-[#E8C47C]";

export const RedeemCode = ({ onDone }) => {
  const [code, setCode] = useState("");
  const { refresh } = useAuth();
  const redeem = async () => {
    try {
      const { data } = await api.post("/coupons/redeem", { code });
      toast.success(`¡Listo! ${data.months} mes(es) de ${data.plan_name} activados`);
      setCode(""); refresh(); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-6 flex flex-wrap gap-3 items-center" data-testid="redeem-box">
      <Ticket className="h-5 w-5 text-[#E8C47C]" />
      <p className="text-sm text-zinc-300 mr-auto">¿Te regalaron meses gratis? Canjea tu código aquí.</p>
      <input data-testid="redeem-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CÓDIGO" className={`${field} font-mono-tc w-48`} />
      <Button data-testid="redeem-btn" disabled={!code} onClick={redeem} className="rounded-full bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold">Canjear</Button>
    </div>
  );
};

const EMPTY = { code: "", type: "percent", value: 20, plan: "", max_uses: "", expires_days: "" };

export const CouponAdmin = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState(EMPTY);
  const load = () => api.get("/admin/coupons").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { ...f, code: f.code.toUpperCase(), value: Number(f.value), plan: f.plan || null,
        max_uses: f.max_uses ? Number(f.max_uses) : null, expires_days: f.expires_days ? Number(f.expires_days) : null });
      toast.success(`Código ${f.code.toUpperCase()} creado`); setF(EMPTY); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const disable = async (code) => { await api.delete(`/admin/coupons/${code}`); toast.success("Código desactivado"); load(); };
  const enable = async (code) => { await api.post(`/admin/coupons/${code}/enable`); toast.success("Código reactivado"); load(); };

  return (
    <div className="panel mt-4 p-5" data-testid="coupon-admin">
      <p className="text-white font-medium mb-4">Códigos de descuento y regalo</p>
      <form onSubmit={create} className="grid sm:grid-cols-3 lg:grid-cols-7 gap-2 items-center">
        <input data-testid="coupon-code-input" required value={f.code} onChange={set("code")} placeholder="CÓDIGO (ej. AMIGOS20)" className={`${field} font-mono-tc lg:col-span-2`} />
        <select data-testid="coupon-type-select" value={f.type} onChange={set("type")} className={field}>
          <option value="percent">% descuento</option><option value="months">Meses gratis</option>
        </select>
        <input data-testid="coupon-value-input" type="number" min={1} max={100} required value={f.value} onChange={set("value")} placeholder={f.type === "percent" ? "%" : "meses"} className={field} />
        <select data-testid="coupon-plan-select" value={f.plan} onChange={set("plan")} className={field}>
          <option value="">{f.type === "percent" ? "Todos los planes" : "Elige plan"}</option>
          <option value="creador">Creador</option><option value="pro">Pro Studio</option><option value="agencia">Agencia</option>
        </select>
        <input data-testid="coupon-maxuses-input" type="number" min={1} value={f.max_uses} onChange={set("max_uses")} placeholder="Usos máx." className={field} />
        <div className="flex gap-2">
          <input data-testid="coupon-days-input" type="number" min={1} value={f.expires_days} onChange={set("expires_days")} placeholder="Días" className={`${field} w-20`} />
          <Button data-testid="coupon-create-btn" className="h-10 bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold">Crear</Button>
        </div>
      </form>
      <div className="mt-5 divide-y divide-white/5 text-sm">
        {list.length === 0 ? <p className="text-zinc-500 py-3">Aún no hay códigos.</p> : list.map((c) => (
          <div key={c.code} className="flex flex-wrap gap-4 items-center py-2.5" data-testid={`coupon-row-${c.code}`}>
            <span className="font-mono-tc text-white">{c.code}</span>
            <span className="text-[#E8C47C]">{c.type === "percent" ? `-${c.value}%` : `${c.value} mes(es) gratis`}{c.plan ? ` · ${c.plan}` : ""}</span>
            <span className="text-zinc-500">{c.uses}/{c.max_uses ?? "∞"} usos</span>
            <span className="text-zinc-500">{c.expires_at ? `vence ${new Date(c.expires_at).toLocaleDateString("es")}` : "sin vencimiento"}</span>
            <span className={`text-xs font-mono-tc ${c.active ? "text-emerald-400" : "text-zinc-600"}`}>{c.active ? "ACTIVO" : "INACTIVO"}</span>
            <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" className="h-8" data-testid={`coupon-copy-${c.code}`} onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Copiado"); }}><Copy className="h-3.5 w-3.5" /></Button>
              {c.active ? <Button size="sm" variant="ghost" className="h-8" data-testid={`coupon-disable-${c.code}`} onClick={() => disable(c.code)}><Ban className="h-3.5 w-3.5 text-zinc-500" /></Button>
                : <Button size="sm" variant="ghost" className="h-8 text-xs text-emerald-400" data-testid={`coupon-enable-${c.code}`} onClick={() => enable(c.code)}>Reactivar</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
